import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Loader2, MessageSquare, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { IdeaLogoBadge } from "../../components/IdeaLogoBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { StatusBadge } from "../../components/StatusBadge";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate, fmtDateTime } from "../../lib/statuses";

const MAIN = ["UNDER REVIEW", "APPROVED TO SEND", "ON THE WAY TO IDEA", "RECEIVED BY IDEA", "IN-STORE TESTING", "ON THE WAY TO SUPPLIER", "AWAITING SUPPLIER RESPONSE"];
const DECISION_SET = ["STORE CREDIT APPROVED", "REPLACEMENT APPROVED", "WARRANTY REPAIR APPROVED", "SUPPLIER REJECTED"];
const DAYS_NOTE = ["STORE CREDIT APPROVED", "REPLACEMENT APPROVED", "WARRANTY REPAIR APPROVED"];

export default function WarrantyTrack() {
  const [rma, setRma] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [claim, setClaim] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!rma || !mobile) { toast.error("Enter both RMA number and mobile"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/warranty/track", { rma_number: rma, mobile });
      setClaim(data);
    } catch (err) {
      setClaim(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Not found");
    } finally { setLoading(false); }
  };

  // Build the dynamic path with decision branch
  let path = [...MAIN];
  let decisionNode = "SUPPLIER DECISION";
  const cur = claim?.current_status;
  if (cur && DECISION_SET.includes(cur)) decisionNode = cur;
  else if (cur === "REJECTED") decisionNode = "REJECTED";
  path.push(decisionNode);
  const rejected = decisionNode === "SUPPLIER REJECTED" || decisionNode === "REJECTED";
  if (!rejected) path.push("RECEIVED & READY TO COLLECT", "COMPLETED");
  const curIndex = cur ? path.indexOf(cur) : -1;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <Link to="/warranty" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Back to portal</Link>
        <div className="mb-6 flex items-center gap-3">
          <IdeaLogoBadge />
          <h1 className="font-display text-xl font-bold text-slate-900">Track Your Claim</h1>
        </div>

        <form onSubmit={search} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">RMA Number</Label>
              <Input data-testid="track-rma-input" value={rma} onChange={(e) => setRma(e.target.value)} placeholder="RMA-2026-000125" className="font-mono" />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">WhatsApp / Mobile Number</Label>
              <Input data-testid="track-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+968 ..." />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="track-submit-btn" className="mt-4 w-full gap-2 bg-blue-600 hover:bg-blue-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Track Claim
          </Button>
        </form>

        {claim && (
          <div data-testid="track-result" className="mt-6 animate-fade-up space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-mono text-xl font-bold text-slate-900">{claim.rma_number}</div>
                  <div className="text-sm text-slate-500">{claim.product_name}</div>
                </div>
                <StatusBadge status={claim.current_status} testId="track-status-badge" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <Info label="Serial" value={claim.serial_number} mono />
                <Info label="Submitted" value={fmtDate(claim.created_at)} />
                <Info label="Last Update" value={fmtDate(claim.last_update)} />
                <Info label="Status" value={claim.current_status} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="mb-5 font-display text-base font-semibold text-slate-900">Progress</h3>
              <div className="space-y-1">
                {path.map((m, i) => {
                  const done = curIndex > i;
                  const active = curIndex === i;
                  const isReject = (m === "SUPPLIER REJECTED" || m === "REJECTED");
                  return (
                    <div key={m + i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs ${
                          active ? (isReject ? "border-rose-500 bg-rose-500 text-white" : "border-blue-600 bg-blue-600 text-white")
                            : done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-300"
                        }`}>
                          {isReject && active ? <XCircle className="h-3.5 w-3.5" /> : (done || active) ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                        </div>
                        {i < path.length - 1 && <div className={`h-6 w-0.5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
                      </div>
                      <div className="pb-5 pt-0.5">
                        <div className={`text-sm font-medium ${active ? "text-slate-900" : done ? "text-slate-700" : "text-slate-400"}`}>{m}</div>
                        {active && DAYS_NOTE.includes(m) && (
                          <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700"><Clock className="h-3 w-3" /> 4–7 WORKING DAYS</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {claim.messages?.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-slate-900"><MessageSquare className="h-4 w-4 text-blue-600" /> Messages from IDEA</h3>
                <div className="space-y-3">
                  {claim.messages.map((m, i) => (
                    <div key={i} className="rounded-xl bg-blue-50 p-3">
                      <p className="text-sm text-slate-700">{m.message}</p>
                      <p className="mt-1 text-xs text-slate-400">{m.sender || "IDEA Warranty Team"} • {fmtDateTime(m.created_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}
